import {
  highlightTerms,
  matchesQuery,
  parseQuery,
  scoreMatch,
  type ParsedQuery,
  type Searchable,
} from './searchQuery';

/**
 * An inverted index over the library.
 *
 * The previous search ran `content.includes()` across every document on every
 * keystroke. That is linear in the size of the whole vault per character typed.
 * Here, tokens are indexed once on write and a query starts from the smallest
 * matching posting list, so cost tracks the number of *candidates* rather than
 * the size of the library.
 *
 * The index is in-memory and rebuilt each session — it is derived from
 * documents, so persisting it would only add a way for it to be wrong.
 */

/** What the index stores per document. */
export interface IndexedDoc extends Searchable {
  id: string;
  updatedAt: number;
  pinned?: boolean;
}

/** A ranked search result. */
export interface IndexHit {
  id: string;
  score: number;
  /** Context around the first match in the body, or null for a title-only hit. */
  snippet: string | null;
}

const TOKEN_RE = /[\p{L}\p{N}_]+/gu;

/** Split text into lowercased word tokens. */
export function tokenize(text: string): string[] {
  return text.toLowerCase().match(TOKEN_RE) ?? [];
}

export class SearchIndex {
  private docs = new Map<string, IndexedDoc>();
  private postings = new Map<string, Set<string>>();

  get size(): number {
    return this.docs.size;
  }

  /** Add or replace a document. */
  upsert(doc: IndexedDoc): void {
    if (this.docs.has(doc.id)) this.remove(doc.id);
    this.docs.set(doc.id, doc);
    for (const token of this.tokensOf(doc)) {
      let set = this.postings.get(token);
      if (!set) {
        set = new Set();
        this.postings.set(token, set);
      }
      set.add(doc.id);
    }
  }

  remove(id: string): void {
    const doc = this.docs.get(id);
    if (!doc) return;
    for (const token of this.tokensOf(doc)) {
      const set = this.postings.get(token);
      if (!set) continue;
      set.delete(id);
      if (set.size === 0) this.postings.delete(token);
    }
    this.docs.delete(id);
  }

  clear(): void {
    this.docs.clear();
    this.postings.clear();
  }

  private tokensOf(doc: IndexedDoc): Set<string> {
    return new Set([
      ...tokenize(doc.title),
      ...tokenize(doc.body),
      ...doc.tags.flatMap((t) => tokenize(t)),
    ]);
  }

  /**
   * Documents that could match a term, using the posting lists.
   *
   * The final term is expanded by prefix because the user is usually still
   * typing it — "mark" has to find "markbird" before the "b" arrives. Returning
   * `null` means "no useful narrowing", and the caller falls back to a scan.
   */
  private candidates(term: string, prefix: boolean): Set<string> | null {
    const tokens = tokenize(term);
    if (tokens.length === 0) return null;

    let narrowed: Set<string> | null = null;
    tokens.forEach((token, i) => {
      const isLast = i === tokens.length - 1;
      let ids: Set<string>;
      if (prefix && isLast) {
        ids = new Set();
        for (const [indexed, set] of this.postings) {
          if (indexed.startsWith(token)) for (const id of set) ids.add(id);
        }
      } else {
        ids = this.postings.get(token) ?? new Set();
      }
      narrowed = narrowed === null ? ids : intersect(narrowed, ids);
    });
    return narrowed;
  }

  /** Run a parsed query, ranked best first. */
  search(rawQuery: string, limit = 200): IndexHit[] {
    const q = parseQuery(rawQuery);
    if (q.empty) return [];

    // Start from the term with the fewest candidates, so the expensive checks
    // run over the smallest possible set.
    let pool: Set<string> | null = null;
    for (let i = 0; i < q.include.length; i++) {
      const term = q.include[i]!;
      if (term.value.startsWith('tag:')) continue;
      // Only the last term is treated as still being typed.
      const ids = this.candidates(term.value, i === q.include.length - 1);
      if (ids === null) continue;
      pool = pool === null ? ids : intersect(pool, ids);
      if (pool.size === 0) return [];
    }

    const ids = pool ?? new Set(this.docs.keys());
    const hits: IndexHit[] = [];
    for (const id of ids) {
      const doc = this.docs.get(id);
      if (!doc || !matchesQuery(doc, q)) continue;
      hits.push({
        id,
        score: scoreMatch(doc, q),
        snippet: snippetFor(doc, q),
      });
    }

    hits.sort(
      (a, b) =>
        b.score - a.score ||
        (this.docs.get(b.id)?.updatedAt ?? 0) - (this.docs.get(a.id)?.updatedAt ?? 0),
    );
    return hits.slice(0, limit);
  }
}

function intersect(a: Set<string>, b: Set<string>): Set<string> {
  const [small, large] = a.size <= b.size ? [a, b] : [b, a];
  const out = new Set<string>();
  for (const id of small) if (large.has(id)) out.add(id);
  return out;
}

const SNIPPET_RADIUS = 40;

/** Context around the first body match, or null when only the title matched. */
export function snippetFor(doc: Searchable, q: ParsedQuery): string | null {
  const terms = highlightTerms(q);
  if (terms.length === 0) return null;
  const lower = doc.body.toLowerCase();
  let pos = -1;
  let hitLength = 0;
  for (const term of terms) {
    const i = lower.indexOf(term);
    if (i >= 0 && (pos < 0 || i < pos)) {
      pos = i;
      hitLength = term.length;
    }
  }
  if (pos < 0) return null;

  const start = Math.max(0, pos - SNIPPET_RADIUS);
  const end = Math.min(doc.body.length, pos + hitLength + SNIPPET_RADIUS);
  let snippet = doc.body.slice(start, end).replace(/\s+/g, ' ').trim();
  if (start > 0) snippet = `… ${snippet}`;
  if (end < doc.body.length) snippet = `${snippet} …`;
  return snippet;
}
